import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectInputProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
}

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
  ({ label, options, placeholder = 'Select...', onChange, value, className, disabled, ...props }, ref) => {
    const id = useId();

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    };

    return (
      <div className={cn('space-y-1.5', className)}>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>

        <div className="relative">
          <select
            ref={ref}
            id={id}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            className={cn(
              // Base styles
              'block w-full rounded-lg px-3.5 py-3 pr-10',
              'text-gray-900 text-base font-medium',
              'appearance-none',
              // Border and background
              'bg-white border-2 border-gray-200',
              'hover:border-gray-300',
              'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10',
              // Transitions
              'transition-all duration-150',
              // Disabled state
              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200',
              // Focus outline removal
              'focus:outline-none',
              // Empty state
              !value && 'text-gray-400'
            )}
            {...props}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom dropdown arrow */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>
    );
  }
);

SelectInput.displayName = 'SelectInput';
